import type {FormEvent} from 'react';
import React, {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'next-i18next';
import Router from 'next/router';
import {
	Alert,
	AlertDescription,
	AlertIcon,
	Box,
	Button,
	FormControl,
	FormErrorMessage,
	FormLabel,
	Heading,
	HStack,
	IconButton,
	Input,
	InputGroup,
	InputRightElement,
	SimpleGrid,
	Text,
	VStack,
} from '@tempo/core';
import {useToast} from '@deezer/next/shared/hooks/useToast';
import {NavLink} from '@tempo/nextjs';
import useEmailValidator from '@deezer/next/shared/hooks/useEmailValidator';
import {ClaimAccessType} from '@deezer/next/features/ClaimAccess/types';
import DisclaimerTerms from '@deezer/next/features/Authentication/components/DisclaimerTerms';
import {CrossCircleOutlinedIcon} from '@tempo/icons';
import ClaimTypeButton from '@deezer/next/features/ClaimAccess/forms/ClaimType/ClaimTypeButton';
import StarmaniaApi from '@deezer/next/services/api/Starmania';
import {Routes} from '@constants/routes';
import {getErrorContent} from '@deezer/next/services/api/utils';
import NavigationLayout from '@deezer/next/shared/layouts/NavigationLayout';
import SmallContentLayout from '@deezer/next/shared/layouts/SmallContentLayout';
import {deezerRegex} from '@constants/regex';
import {getServerSideTranslationsWrapper} from '@deezer/next/shared/utils/next';

const profileTypes = Object.values(ClaimAccessType).filter(i =>
	isNaN(Number(i)),
);

const SignUp = () => {
	const {t} = useTranslation('d4c');
	const [email, setEmail] = useState('');
	const [isDeezerEmail, setIsDeezerEmail] = useState(false);
	const [emailError, validateEmail] = useEmailValidator(500);
	const [name, setName] = useState('');
	const [claimType, setClaimType] = useState<ClaimAccessType | undefined>(
		undefined,
	);
	const [isLoading, setIsLoading] = useState(false);
	const toast = useToast();

	const alertDescription = useMemo(() => {
		switch (claimType) {
			case ClaimAccessType.PROVIDER:
				return t('signuppage_text_fordigitalproviders_d4c-web');
			case ClaimAccessType.LABEL:
				return t('signuppage_text_forrecordlabels_d4c-web');
			case ClaimAccessType.ARTIST:
				return t('signuppage_text_forartists_d4c-web');
			case ClaimAccessType.MANAGER:
				return t('signuppage_text_formanagers_d4c-web');
			default:
				// fixme : translate
				return 'Select a valid role';
		}
	}, [claimType, t]);

	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const email = e.target.value;
		setEmail(email);
		setIsDeezerEmail(deezerRegex.test(email));
		validateEmail(email);
	};

	const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setName(e.target.value);
	};

	const handleClaimTypeChange = (claimType: ClaimAccessType) => {
		setClaimType(claimType);
	};

	const handleRemoveEmailValue = () => {
		setEmail('');
	};

	const handleRemoveNameValue = () => {
		setName('');
	};

	const handleSignUp = useCallback(
		async (event: FormEvent<HTMLDivElement>) => {
			event.preventDefault();
			if (!claimType) return;
			// TODO : handle this case on test auto
			//  if user delete part of the email and click on submit
			//  button too fast (500ms), this error occurs
			const hasEmailError = validateEmail(email);
			if (hasEmailError) return;

			setIsLoading(true);

			try {
				await StarmaniaApi.auth.signUp(name, email, claimType);
				Router.push({
					pathname: Routes.EMAIL_CONFIRMATION,
					query: {
						q: window.btoa(
							JSON.stringify({
								name,
								email,
								claimType,
								from: Router.pathname,
							}),
						),
					},
				});
			} catch (e: any) {
				const errorCode = Number(e.response.data.errors?.[0]?.code);
				const {title, description} = getErrorContent(errorCode, t);
				toast({
					title,
					description,
					status: 'error',
				});
			} finally {
				setIsLoading(false);
			}
		},
		[claimType, validateEmail, email, name, t, toast],
	);

	return (
		<VStack as="form" onSubmit={handleSignUp} spacing="spacing.3xl">
			<VStack spacing="spacing.m">
				<Heading as="h1" variant="title.screen.mobile">
					{t('signuppage_title_signup_d4c-web')}
				</Heading>
				<HStack spacing="spacing.xs">
					<Text variant="body.small.regular">
						{t('signuppage_text_doyouhaveanaccount_d4c-web')}
					</Text>
					<NavLink
						href={Routes.LOGIN}
						data-testid="login-button"
						variant="body.s.underlined"
					>
						{t('generic_action_login_d4c-web')}
					</NavLink>
				</HStack>
			</VStack>
			<VStack spacing="spacing.xl" width="full">
				<FormControl isRequired={true} alignItems="flex-start" width="100%">
					<FormLabel as="h2">
						{t('signuppage_title_selectyourrole_d4c-web')}
					</FormLabel>
					<SimpleGrid columns={{base: 2, sm: 4}} spacing={8} width="100%">
						{profileTypes.map(c => (
							<ClaimTypeButton
								key={c}
								claimType={c}
								isSelected={c === claimType}
								onClick={handleClaimTypeChange}
							/>
						))}
					</SimpleGrid>
				</FormControl>
				{claimType && (
					<>
						<Alert w={{base: '90vw', sm: '70vw', md: '50vw'}} status="info">
							<AlertIcon />
							<Box flex="1" me="spacing.m">
								<AlertDescription>{alertDescription}</AlertDescription>
							</Box>
						</Alert>
						<FormControl isRequired={true}>
							<FormLabel htmlFor="name">
								{t('analyticsartist_text_name_d4c-web')}
							</FormLabel>
							<InputGroup>
								<Input
									data-testid="sign-up-name-input"
									id="name"
									name="name"
									type="text"
									value={name}
									onChange={handleNameChange}
								/>
								<InputRightElement>
									<IconButton
										variant="ghost"
										aria-label="Remove name"
										icon={<CrossCircleOutlinedIcon boxSize={6} />}
										disabled={!name}
										onClick={handleRemoveNameValue}
									/>
								</InputRightElement>
							</InputGroup>
						</FormControl>

						<FormControl isRequired={true} isInvalid={emailError}>
							<FormLabel htmlFor="email">
								{t('usermanager_text_email_d4c-web')}
							</FormLabel>
							<InputGroup>
								<Input
									data-testid="sign-up-email-input"
									id="email"
									name="email"
									type="email"
									value={email}
									onChange={handleEmailChange}
								/>
								<InputRightElement>
									<IconButton
										variant="ghost"
										aria-label="Remove email"
										icon={<CrossCircleOutlinedIcon boxSize={6} />}
										disabled={!email}
										onClick={handleRemoveEmailValue}
									/>
								</InputRightElement>
							</InputGroup>
							<FormErrorMessage>
								{isDeezerEmail
									? t(
											'oktaautherrormessage_text_emailcantcontaindeezercom_d4c-web',
										)
									: emailError && t('errormessage_title_emailnotvalid_d4c-web')}
							</FormErrorMessage>
						</FormControl>
						<DisclaimerTerms />
						<Button
							data-testid="create-account-button"
							width="full"
							isLoading={isLoading}
							type="submit"
							disabled={
								!email || !name || !claimType || emailError || isDeezerEmail
							}
						>
							{t('generic_action_createmyaccount_d4c-web')}
						</Button>
					</>
				)}
			</VStack>
		</VStack>
	);
};

SignUp.getLayout = (page: React.ReactElement) => {
	return (
		<NavigationLayout useHorizontalNavbar={true}>
			<SmallContentLayout>{page}</SmallContentLayout>
		</NavigationLayout>
	);
};

export const getStaticProps = getServerSideTranslationsWrapper(() => {
	return {
		props: {
			unlogged: true,
			mode: 'dark',
		},
	};
});

export default SignUp;
